/**
 * InfoBanner Component
 * Standardized informational banner for displaying messages, warnings, filters, and alerts
 *
 * Usage:
 * <InfoBanner
 *   variant="info"
 *   icon={TrendingUp}
 *   message="Focus on these 7 customer-sector segments for maximum impact"
 * />
 *
 * <InfoBanner
 *   variant="filter"
 *   message='Filtering: "8917"'
 *   action={{ label: "Clear filter", onClick: handleClear }}
 * />
 */

import React from 'react'
import clsx from 'clsx'
import { LucideIcon } from 'lucide-react'

export type InfoBannerVariant = 'info' | 'warning' | 'success' | 'error' | 'filter'

interface InfoBannerAction {
  label: string
  onClick: () => void
}

interface InfoBannerProps {
  variant: InfoBannerVariant
  message: string | React.ReactNode
  title?: string
  icon?: LucideIcon
  action?: InfoBannerAction
  className?: string
}

const variantStyles: Record<InfoBannerVariant, {
  container: string
  icon: string
  title: string
  message: string
  actionButton: string
}> = {
  info: {
    container: 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-900 dark:text-blue-200',
    message: 'text-blue-800 dark:text-blue-300',
    actionButton: 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline'
  },
  warning: {
    container: 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700',
    icon: 'text-yellow-600 dark:text-yellow-500',
    title: 'text-yellow-900 dark:text-yellow-200',
    message: 'text-yellow-800 dark:text-yellow-300',
    actionButton: 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 hover:underline'
  },
  success: {
    container: 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-900 dark:text-green-200',
    message: 'text-green-800 dark:text-green-300',
    actionButton: 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:underline'
  },
  error: {
    container: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-900 dark:text-red-200',
    message: 'text-red-800 dark:text-red-300',
    actionButton: 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:underline'
  },
  filter: {
    container: 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800',
    icon: 'text-indigo-600 dark:text-indigo-400',
    title: 'text-indigo-900 dark:text-indigo-200',
    message: 'text-indigo-800 dark:text-indigo-300',
    actionButton: 'text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 hover:underline'
  }
}

export const InfoBanner: React.FC<InfoBannerProps> = ({
  variant,
  message,
  title,
  icon: Icon,
  action,
  className
}) => {
  const styles = variantStyles[variant]

  return (
    <div className={clsx(styles.container, 'rounded-lg p-3', className)} role="alert">
      <div className="flex items-start gap-2">
        {Icon && (
          <div className="flex-shrink-0">
            <Icon className={clsx('h-4 w-4', styles.icon)} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className={clsx('text-sm font-medium mb-1', styles.title)}>
              {title}
            </h3>
          )}
          <div className={clsx('text-sm', styles.message, !title && 'font-medium')}>
            {message}
          </div>
        </div>
        {action && (
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={action.onClick}
              className={clsx('text-sm font-medium', styles.actionButton)}
            >
              {action.label}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default InfoBanner
